import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiPackage,
  FiDollarSign,
  FiClock,
  FiBox,
  FiInfo,
  FiLayers,
  FiCheckCircle
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useFormulaciones } from '../../hooks/useFormulaciones';

const UNIT_TO_BASE = {
  KG: 1000, G: 1, MG: 0.001, MCG: 0.000001,
  L: 1000, ML: 1,
  UNIT: 1, BOTTLE: 1, BOX: 1, PACKAGE: 1,
};

const FormulacionDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    getFormulacionById,
  } = useFormulaciones();

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadFormulacion();
    }
  }, [id]);

  const loadFormulacion = async () => {
    setIsLoading(true);
    try {
      const result = await getFormulacionById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        setError(result.message || 'Error al cargar formulación');
      }
    } catch (error) {
      setError('Error de conexión al cargar formulación');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    if (formData && formData.result_product_id) {
      navigate(`/system/productos/${formData.result_product_id}/editar`);
    } else {
      navigate(`/system/formulaciones/${id}/editar`);
    }
  };

  const handleBack = () => {
    navigate('/system/formulaciones');
  };

  const isDiscreteUnit = useCallback(() => {
    if (!formData) return true;
    const unit = formData.quantity_unit;
    return unit === 'UNIT';
  }, [formData]);

  const calculateTotals = useCallback(() => {
    if (!formData || !formData.ingredients) {
      return { ingredientCost: 0, containerCost: 0, lidCost: 0, laborCost: 0, totalCost: 0, costPerUnit: 0 };
    }

    const effectiveQpc = isDiscreteUnit()
      ? parseFloat(formData.quantity_per_container || 1)
      : 1;

    // Costo de ingredientes por envase
    let ingredientCost = 0;
    formData.ingredients.forEach(ing => {
      const qty = parseFloat(ing.quantity_per_unit) || 0;
      let unitCost = parseFloat(ing.cost_per_unit) || 0;

      // Si no hay cost_per_unit pero hay cost_price del producto ingrediente, convertir unidades
      if (!unitCost && ing.cost_price) {
        const costPrice = parseFloat(ing.cost_price);
        const baseUnit = ing.base_unit || ing.unit;
        const formulaUnit = ing.unit || 'G';
        const factorBase = UNIT_TO_BASE[baseUnit] || 1;
        const factorFormula = UNIT_TO_BASE[formulaUnit] || 1;
        unitCost = costPrice * (factorFormula / factorBase);
      }

      ingredientCost += qty * unitCost * effectiveQpc;
    });

    // Costos adicionales
    const containerCost = formData.container_product_cost ? parseFloat(formData.container_product_cost) : 0;
    const lidCost = formData.lid_product_cost ? parseFloat(formData.lid_product_cost) : 0;
    const laborCost = parseFloat(formData.labor_cost_per_hour) || 0;

    const totalCost = ingredientCost + containerCost + lidCost + laborCost;
    const costPerUnit = isDiscreteUnit() && formData.quantity_per_container > 0
      ? totalCost / parseFloat(formData.quantity_per_container)
      : totalCost;

    return { ingredientCost, containerCost, lidCost, laborCost, totalCost, costPerUnit };
  }, [formData, isDiscreteUnit]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount || 0);
  };

  const getCapsuleTypeLabel = (type) => {
    const types = {
      'VEGETARIAN': 'Vegetarianas',
      'GELATIN': 'Gelatina',
      'NOT_APPLICABLE': 'No Aplica'
    };
    return types[type] || type || 'No Aplica';
  };

  const getFormulaTypeLabel = (type) => {
    const types = {
      'BETEL': 'Betel Natural',
      'NATURALANDLAB': 'Natural & Lab',
      'COTIZACIONES': 'Cotizaciones',
      'EXCLUSIVE': 'Exclusiva'
    };
    return types[type] || type;
  };

  const getUnitLabel = (unit) => {
    const units = {
      'UNIT': 'Unidades', 'G': 'Gramos', 'KG': 'Kilogramos',
      'MG': 'Miligramos', 'ML': 'Mililitros', 'L': 'Litros'
    };
    return units[unit] || unit;
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando formulación..." />;
  }

  if (error || !formData) {
    return (
      <div className="factura-form-page">
        <div className="alert alert--danger">
          <strong>Error:</strong> {error || 'No se pudo cargar la formulación'}
        </div>
        <button className="btn btn--outline" onClick={handleBack}>
          <FiArrowLeft size={16} />
          Volver
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleBack}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {formData.formula_name || `Formulación #${id}`}
        </h1>

        <div className="header-actions">
          <button className="btn btn--primary" onClick={handleEdit}>
            <FiEdit size={16} />
            Editar
          </button>
        </div>
      </div>

      {/* Card 1: Información Principal */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiLayers size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">
              {formData.formula_name}
            </h3>
            <p className="invoice-header-subtitle">
              {formData.company_name} • {getFormulaTypeLabel(formData.formula_type)}
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Compañía */}
          <div className="invoice-field">
            <div className="invoice-field-icon"><FiBox size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Compañía</label>
              <div className="invoice-field-value">{formData.company_name}</div>
            </div>
          </div>

          {/* Tipo de Envase */}
          <div className="invoice-field">
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipo de Envase</label>
              <div className="invoice-field-value">{formData.container_product_name || 'No asignado'}</div>
            </div>
          </div>

          {/* Tapadera */}
          {formData.lid_product_name && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiPackage size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tapadera</label>
                <div className="invoice-field-value">{formData.lid_product_name}</div>
              </div>
            </div>
          )}

          {/* Cantidad por Envase */}
          <div className="invoice-field">
            <div className="invoice-field-icon"><FiPackage size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Cantidad por Envase</label>
              <div className="invoice-field-value">{formData.quantity_per_container} {getUnitLabel(formData.quantity_unit)}</div>
            </div>
          </div>

          {/* Tipo de Cápsula - Solo si aplica */}
          {formData.capsule_type && formData.capsule_type !== 'NOT_APPLICABLE' && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiPackage size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tipo de Cápsula</label>
                <div className="invoice-field-value">{getCapsuleTypeLabel(formData.capsule_type)}</div>
              </div>
            </div>
          )}

          {/* Ingrediente Cápsula */}
          {formData.capsule_ingredient_name && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiPackage size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Ingrediente Cápsula</label>
                <div className="invoice-field-value">{formData.capsule_ingredient_name}</div>
              </div>
            </div>
          )}

          {/* Tiempo de Mezcla */}
          <div className="invoice-field">
            <div className="invoice-field-icon"><FiClock size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tiempo de Mezcla</label>
              <div className="invoice-field-value">{formData.mixing_time_minutes || 0} minutos</div>
            </div>
          </div>

          {/* Mano de Obra */}
          {formData.labor_cost_per_hour > 0 && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiDollarSign size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Mano de Obra</label>
                <div className="invoice-field-value">{formatCurrency(formData.labor_cost_per_hour)}</div>
              </div>
            </div>
          )}

          {/* Mejor Uso */}
          {formData.best_use_date_years && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiInfo size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Mejor Uso</label>
                <div className="invoice-field-value">
                  {(() => {
                    const d = new Date(formData.best_use_date_years + 'T00:00:00');
                    if (isNaN(d.getTime())) return formData.best_use_date_years;
                    const now = new Date();
                    const diffMonths = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
                    const dateStr = d.toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' });
                    return diffMonths > 0 ? `${diffMonths} meses (${dateStr})` : dateStr;
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Etiqueta */}
          {formData.label_product_name && (
            <div className="invoice-field">
              <div className="invoice-field-icon"><FiInfo size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Material de Etiqueta</label>
                <div className="invoice-field-value">{formData.label_product_name}</div>
              </div>
            </div>
          )}

          {/* Sílica */}
          <div className="invoice-field">
            <div className="invoice-field-icon"><FiCheckCircle size={18} /></div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Sílica</label>
              <div className="invoice-field-value">
                {formData.includes_silica ? `Incluida - ${formatCurrency(formData.silica_cost)}` : 'No incluida'}
              </div>
            </div>
          </div>

          {/* Notas */}
          {formData.notes && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon"><FiInfo size={18} /></div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <div className="invoice-field-value">{formData.notes}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Tabla de Ingredientes */}
      {formData.ingredients && formData.ingredients.length > 0 ? (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon"><FiLayers size={24} /></div>
            <div className="table-header-content">
              <h3 className="table-header-title">Ingredientes de la Formulación</h3>
              <p className="table-header-subtitle">{formData.ingredients.length} ingrediente(s)</p>
            </div>
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th className="th-product">Ingrediente</th>
                  <th className="th-qty">Cantidad</th>
                  <th className="th-price">Unidad</th>
                  <th className="th-price">Costo/U</th>
                  <th className="th-total">Total</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {formData.ingredients.map((ingredient, index) => {
                  const qty = parseFloat(ingredient.quantity_per_unit) || 0;
                  let unitCost = parseFloat(ingredient.cost_per_unit) || 0;

                  if (!unitCost && ingredient.cost_price) {
                    const costPrice = parseFloat(ingredient.cost_price);
                    const baseUnit = ingredient.base_unit || ingredient.unit;
                    const formulaUnit = ingredient.unit || 'G';
                    const factorBase = UNIT_TO_BASE[baseUnit] || 1;
                    const factorFormula = UNIT_TO_BASE[formulaUnit] || 1;
                    unitCost = costPrice * (factorFormula / factorBase);
                  }

                  const effectiveQpc = isDiscreteUnit()
                    ? parseFloat(formData.quantity_per_container || 1)
                    : 1;
                  const total = qty * unitCost * effectiveQpc;

                  return (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon"><FiPackage size={16} /></div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">{ingredient.ingredient_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <span className="qty-badge">{qty}</span>
                      </td>
                      <td className="td-price">
                        <span className="price-value">{ingredient.unit}</span>
                      </td>
                      <td className="td-price">
                        <span className="price-value">{formatCurrency(unitCost)}</span>
                      </td>
                      <td className="td-total">
                        <span className="total-value">{formatCurrency(total)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="invoice-totals-section">
            <div className="totals-container">
              <div className="totals-row">
                <span className="totals-label">Costo Ingredientes</span>
                <span className="totals-value">{formatCurrency(totals.ingredientCost)}</span>
              </div>
              {totals.containerCost > 0 && (
                <div className="totals-row">
                  <span className="totals-label">Envase</span>
                  <span className="totals-value">{formatCurrency(totals.containerCost)}</span>
                </div>
              )}
              {totals.lidCost > 0 && (
                <div className="totals-row">
                  <span className="totals-label">Tapadera</span>
                  <span className="totals-value">{formatCurrency(totals.lidCost)}</span>
                </div>
              )}
              {totals.laborCost > 0 && (
                <div className="totals-row">
                  <span className="totals-label">Mano de Obra</span>
                  <span className="totals-value">{formatCurrency(totals.laborCost)}</span>
                </div>
              )}
              <div className="totals-divider"></div>
              <div className="totals-row totals-row--final">
                <span className="totals-label">Costo Total por Envase</span>
                <span className="totals-value">{formatCurrency(totals.totalCost)}</span>
              </div>
              {isDiscreteUnit() && (
                <div className="totals-row">
                  <span className="totals-label">Costo por Unidad</span>
                  <span className="totals-value">{formatCurrency(totals.costPerUnit)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon"><FiLayers size={64} /></div>
          <h3 className="empty-state-title">No hay ingredientes</h3>
          <p className="empty-state-message">
            Esta formulación no tiene ingredientes registrados
          </p>
        </div>
      )}
    </div>
  );
};

export default FormulacionDetalle;
